import React, { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiTrash2, FiEye, FiSearch } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import { resolveMissingPet } from '../../services/adminService';
import styles from './MissingPets.module.css';

// Schema (verified): missingPets.{ userId, petName, petSpecies, petBreed, lastSeenLocation,
// description, status, photos[]/imageUrl, createdAt, ... }

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'found', label: 'Found' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const MissingPets = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(500)], []);
  const { data: missingPets, loading } = useFirestoreCollection('missingPets', constraints);
  const directory = useUserDirectory();

  const filtered = useMemo(() => {
    let list = missingPets;
    if (statusFilter !== 'all') {
      list = list.filter((p) => (p.status || 'active') === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          (p.petName || '').toLowerCase().includes(q) ||
          // The app writes a single free-text `location`, not petSpecies/petBreed.
          (p.location || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.ownerName || '').toLowerCase().includes(q) ||
          directory.searchBlobOf(p.userId).includes(q)
      );
    }
    return list;
  }, [missingPets, statusFilter, searchQuery, directory]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  useEffect(() => {}, []);

  const handleResolve = async (mp) => {
    if (!window.confirm(`Mark "${mp.petName || 'this report'}" as resolved?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await resolveMissingPet(mp.id, { hard: false, notes: 'Marked resolved by admin' });
      setShowDetail(false);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Resolve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (mp) => {
    if (!window.confirm(`Permanently delete the missing-pet report for "${mp.petName || mp.id}"?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await resolveMissingPet(mp.id, { hard: true });
      setShowDetail(false);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const variants = { active: 'warning', resolved: 'success', found: 'success' };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'active'}</Badge>;
  };

  // Schema (verified 2026-08-21): missingPets.{ petName, description, image,
  // images[], location, coordinates, radius, radiusWorldwide, status, likes,
  // commentsCount, reactions, ownerName, ownerAvatar, userId, createdAt }.
  // There is no petSpecies / petBreed / lastSeenLocation / photos field — the
  // old code read all four, so Last Seen and the species line always rendered
  // "-" and the thumbnail never loaded.
  const photoList = (mp) => {
    if (!mp) return [];
    const out = [];
    if (mp.image) out.push(mp.image);
    if (Array.isArray(mp.images)) out.push(...mp.images);
    if (Array.isArray(mp.photos)) out.push(...mp.photos);
    if (mp.imageUrl) out.push(mp.imageUrl);
    if (mp.photo) out.push(mp.photo);
    return [...new Set(out.filter(Boolean))];
  };

  const photoUrl = (mp) => photoList(mp)[0] || null;

  const ownerLabel = (mp) => directory.nameOf(mp?.userId, mp?.ownerName || '');

  const csvColumns = [
    { key: 'id', label: 'Report ID' },
    { key: 'ownerName', label: 'Owner', accessor: (m) => ownerLabel(m) },
    { key: 'ownerEmail', label: 'Owner Email', accessor: (m) => directory.emailOf(m.userId) },
    { key: 'userId', label: 'Reporter UID' },
    { key: 'petName', label: 'Pet Name' },
    { key: 'location', label: 'Last Seen' },
    { key: 'radius', label: 'Search radius (km)', accessor: (m) => (m.radiusWorldwide ? 'worldwide' : m.radius ?? '') },
    { key: 'description', label: 'Description' },
    { key: 'photoCount', label: 'Photos', accessor: (m) => photoList(m).length },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Reported', accessor: (m) => formatDate(m.createdAt) },
  ];

  const columns = [
    {
      key: 'pet',
      title: 'Pet',
      render: (_, row) => (
        <div className={styles.petCell}>
          <div className={styles.petAvatar}>
            {photoUrl(row) ? <img src={photoUrl(row)} alt="" /> : <span>🐾</span>}
          </div>
          <div>
            <div className={styles.petName}>{row.petName || '(no name)'}</div>
            <div className={styles.petMeta}>{ownerLabel(row) || '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      title: 'Last Seen',
      render: (v, row) => (
        <div>
          <div>{v || '-'}</div>
          {(row.radius || row.radiusWorldwide) && (
            <div className={styles.petMeta}>
              {row.radiusWorldwide ? 'worldwide alert' : `${row.radius} km radius`}
            </div>
          )}
        </div>
      ),
    },
    { key: 'status', title: 'Status', render: (v) => statusBadge(v || 'active') },
    { key: 'createdAt', title: 'Reported', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '160px',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            onClick={() => {
              setSelected(row);
              setShowDetail(true);
            }}
          />
          {(row.status || 'active') === 'active' && (
            <Button
              variant="primary"
              size="sm"
              icon={<FiCheck />}
              onClick={() => handleResolve(row)}
              disabled={actionLoading}
            />
          )}
          <Button
            variant="danger"
            size="sm"
            icon={<FiTrash2 />}
            onClick={() => handleDelete(row)}
            disabled={actionLoading}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Missing Pets</h1>
          {!loading && <Badge variant="primary">{missingPets.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Manage missing-pet reports
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by name, breed, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-missing-pets-${statusFilter}`} />
        </div>

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No missing-pet reports found"
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        title="Missing Pet Report"
        size="lg"
        footer={
          selected && (
            <>
              <Button variant="ghost" onClick={() => setShowDetail(false)}>
                Close
              </Button>
              {(selected.status || 'active') === 'active' && (
                <Button variant="primary" icon={<FiCheck />} onClick={() => handleResolve(selected)}>
                  Mark Resolved
                </Button>
              )}
              <Button variant="danger" icon={<FiTrash2 />} onClick={() => handleDelete(selected)}>
                Delete Report
              </Button>
            </>
          )
        }
      >
        {selected && (
          <div className={styles.detailContent}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Pet Name</span>
                <span className={styles.detailValue}>{selected.petName || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Owner</span>
                <span className={styles.detailValue}>{ownerLabel(selected) || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Owner email</span>
                <span className={styles.detailValue}>
                  {directory.emailOf(selected.userId) || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Last Seen</span>
                <span className={styles.detailValue}>{selected.location || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Alert radius</span>
                <span className={styles.detailValue}>
                  {selected.radiusWorldwide
                    ? 'Worldwide'
                    : selected.radius
                      ? `${selected.radius} km`
                      : '-'}
                </span>
              </div>
              {selected.coordinates && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Coordinates</span>
                  <span className={styles.detailValue}>
                    <a
                      href={`https://www.google.com/maps?q=${selected.coordinates.latitude},${selected.coordinates.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {selected.coordinates.latitude?.toFixed?.(5)},{' '}
                      {selected.coordinates.longitude?.toFixed?.(5)}
                    </a>
                  </span>
                </div>
              )}
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reporter UID</span>
                <span className={styles.detailValue}>{selected.userId || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Status</span>
                <span className={styles.detailValue}>{statusBadge(selected.status || 'active')}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reported</span>
                <span className={styles.detailValue}>{formatDate(selected.createdAt)}</span>
              </div>
            </div>

            {selected.description && (
              <div className={styles.section}>
                <h4>Description</h4>
                <p style={{ whiteSpace: 'pre-wrap' }}>{selected.description}</p>
              </div>
            )}

            {photoList(selected).length > 0 && (
              <div className={styles.section}>
                <h4>Photos ({photoList(selected).length})</h4>
                <div className={styles.mediaGrid}>
                  {photoList(selected).map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MissingPets;
